import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarBlank, Plus, Trash, FilePdf, ChatCircleText } from '@phosphor-icons/react';

export default function BookingsPage() {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ property_id: '', guest_name: '', guest_email: '', guest_phone: '', sms_consent: false, check_in: '', check_out: '', num_guests: 1, total_price: 0, notes: '' });
  const [saving, setSaving] = useState(false);
  const [sendingSms, setSendingSms] = useState(null);

  const load = () => {
    Promise.all([api.get('/bookings'), api.get('/properties')])
      .then(([b, p]) => { setBookings(b.data); setProperties(p.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/bookings', form);
      setShowForm(false);
      setForm({ property_id: '', guest_name: '', guest_email: '', guest_phone: '', sms_consent: false, check_in: '', check_out: '', num_guests: 1, total_price: 0, notes: '' });
      load();
    } catch { alert('Error creating booking'); } finally { setSaving(false); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    await api.delete(`/bookings/${id}`);
    load();
  };

  const sendReviewSMS = async (booking) => {
    let phone = booking.guest_phone || '';
    if (!phone) {
      phone = window.prompt(`Enter ${booking.guest_name}'s mobile number to send a Google review request (E.164, e.g. +15551234567):`) || '';
      if (!phone) return;
    }
    setSendingSms(booking.id);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/send-review-request`, { phone });
      alert(`Review-request SMS sent to ${phone} ✓\n\nLink: ${data.link}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(null);
    }
  };

  const statusColor = (s) => {
    if (s === 'confirmed') return 'bg-emerald-50 text-emerald-700';
    if (s === 'cancelled') return 'bg-red-50 text-red-700';
    return 'bg-zinc-100 text-zinc-600';
  };

  return (
    <div data-testid="bookings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Bookings</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage guest reservations</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] flex items-center gap-2" data-testid="add-booking-btn">
          <Plus weight="bold" size={14} /> Add Booking
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowForm(false)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg" data-testid="booking-form-modal">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>New Booking</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property</label>
                <select value={form.property_id} onChange={e => set('property_id', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="booking-property-select">
                  <option value="">Select property</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Guest Name</label>
                  <input value={form.guest_name} onChange={e => set('guest_name', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-guest-name" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Guest Email</label>
                  <input type="email" value={form.guest_email} onChange={e => set('guest_email', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-guest-email" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Guest Mobile (for review-request SMS)</label>
                <input type="tel" placeholder="+15551234567" value={form.guest_phone} onChange={e => set('guest_phone', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-guest-phone" />
              </div>

              {form.guest_phone.trim() && (
                <label className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm cursor-pointer" data-testid="sms-consent-block">
                  <input
                    type="checkbox"
                    checked={form.sms_consent}
                    onChange={e => set('sms_consent', e.target.checked)}
                    required={!!form.guest_phone.trim()}
                    className="mt-0.5 shrink-0 w-4 h-4 accent-[#0052FF]"
                    data-testid="booking-sms-consent"
                  />
                  <span className="text-xs text-zinc-900 leading-relaxed">
                    <span className="font-bold block mb-0.5">Guest SMS Consent (required to use SMS features)</span>
                    The guest agrees to receive transactional SMS messages from HostCockpit related to their stay, including an optional
                    post-checkout message with a link to leave a Google review. Message &amp; data rates may apply. Guest can reply STOP to unsubscribe.
                    {' '}<a href="/sms-consent" target="_blank" rel="noreferrer" className="underline text-[#0052FF]">Full SMS Terms</a>
                  </span>
                </label>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Check-in</label>
                  <input type="date" value={form.check_in} onChange={e => set('check_in', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-checkin" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Check-out</label>
                  <input type="date" value={form.check_out} onChange={e => set('check_out', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-checkout" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Guests</label>
                  <input type="number" min="1" value={form.num_guests} onChange={e => set('num_guests', parseInt(e.target.value) || 1)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-guests" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Total Price ($)</label>
                  <input type="number" min="0" value={form.total_price} onChange={e => set('total_price', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" data-testid="booking-price" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-zinc-200 text-sm font-bold text-zinc-700 rounded-sm hover:bg-zinc-50" data-testid="booking-cancel-btn">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50" data-testid="booking-save-btn">{saving ? 'Saving...' : 'Create Booking'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <p className="text-sm text-zinc-500">Loading...</p> : bookings.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-bookings">
          <CalendarBlank size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No bookings yet</h3>
          <p className="text-sm text-zinc-500">Create your first booking to track guest reservations.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200" data-testid="bookings-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Guest</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Property</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Check-in</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Check-out</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Price</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {bookings.map((b, i) => (
                  <tr key={b.id} className="hover:bg-zinc-50" data-testid={`booking-row-${i}`}>
                    <td className="px-4 py-3 font-bold text-zinc-900">{b.guest_name}</td>
                    <td className="px-4 py-3 text-zinc-600">{b.property_name}</td>
                    <td className="px-4 py-3 text-zinc-600">{b.check_in}</td>
                    <td className="px-4 py-3 text-zinc-600">{b.check_out}</td>
                    <td className="px-4 py-3 font-bold text-zinc-900">${b.total_price}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 font-bold ${statusColor(b.status)}`}>{b.status}</span></td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      <button
                        onClick={() => sendReviewSMS(b)}
                        disabled={sendingSms === b.id}
                        className={`transition-colors ${b.review_request_sent_at ? 'text-emerald-600' : 'text-zinc-400 hover:text-[#0052FF]'}`}
                        title={b.review_request_sent_at ? `Review SMS sent ${b.review_request_sent_at.slice(0,10)}` : 'Send Google review request SMS'}
                        data-testid={`review-sms-${i}`}
                      >
                        <ChatCircleText size={16} weight={b.review_request_sent_at ? 'fill' : 'duotone'} />
                      </button>
                      <button
                        onClick={async () => {
                          const url = `/api/properties/${b.property_id}/welcome-guide?check_in=${b.check_in}&check_out=${b.check_out}`;
                          const res = await fetch(url);
                          const blob = await res.blob();
                          const a = document.createElement('a');
                          a.href = URL.createObjectURL(blob);
                          a.download = `${b.property_name || 'Welcome'}-Guide.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(a.href);
                        }}
                        className="text-[#0052FF] hover:text-[#003EDD] transition-colors"
                        title="Download Welcome Guide with weather"
                        data-testid={`booking-pdf-${i}`}
                      >
                        <FilePdf size={16} weight="duotone" />
                      </button>
                      <button onClick={() => deleteBooking(b.id)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-booking-${i}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
